import React, { useEffect } from "react";
import { useReporting, PlacementOption } from "Hooks/AdminHooks/useReporting";
import SoftButton from "StyleFeatures/SoftButton";
import PropTypes from "prop-types";
import { Card, Select } from "antd";
import { UserOutlined, ArrowDownOutlined, UserAddOutlined } from "@ant-design/icons";
import Footer from "Common/Footer";
import "../ParentComponent/Reporting.scss";

const { Option } = Select;
function Reporting({ onNext, onBack, jobData = {}, initialData = {} }) {
  const designationId = jobData?.designationId || 0;
  const departmentId = jobData?.departmentId || 0;

  const employeeName = jobData?.fullName || "New Employee";
  const employeeDesignation = jobData?.designationName || "New Position";

  const {
    availableSupervisors,
    selectedSupervisor,
    placementOption,
    supervisorDirectReports,
    loadingSupervisors,
    handleSupervisorChange,
    handlePlacementChange,
    validate,
  } = useReporting(designationId, departmentId, initialData);

  console.log("supervirs ", availableSupervisors);

  const canSelectParent = supervisorDirectReports.length > 0;

  useEffect(() => {
    if (supervisorDirectReports.length === 0) {
      handlePlacementChange(PlacementOption.SIBLING);
    }
  }, [supervisorDirectReports]);

  const handleNext = () => {
    if (validate()) {
      onNext({
        selectedSupervisor,
        placementOption,
        fullName: employeeName,
        designationName: employeeDesignation,
      });
    }
  };

  return (
    <div className="body flex-grow-1 px-3 reporting-container">
      <Card title="Place Employee in Hierarchy" className="card-professional">
        {/* Step 1: Select Supervisor */}
        <div className="mb-4">
          <label className="step-header">Step 1: Select Supervisor</label>
          <Select
            showSearch
            placeholder="Search and Select Supervisor"
            className="w-100"
            size="large"
            loading={loadingSupervisors}
            value={selectedSupervisor?.id || undefined}
            onChange={(val) => handleSupervisorChange(val)}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {availableSupervisors.map((sup) => (
              <Option key={sup.id} value={sup.id}>
                {sup.fullName}
              </Option>
            ))}
          </Select>

          {selectedSupervisor && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px",
                border: "1px solid var(--light-focus)", // Light professional green border
                borderRadius: "12px",
                backgroundColor: "var(--white)",
                marginTop: "12px",
                boxShadow: "var(--box-shadow-sm)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "50%",
                    backgroundColor: "var(--bg-default)", // Subtle green background
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--primary-main)", // Icon color
                    fontSize: "20px",
                  }}
                >
                  <UserOutlined />
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: "16px", color: "var(--text-main)" }}>
                    {selectedSupervisor.fullName || "No Supervisor"}
                  </div>
                  <div
                    style={{ fontSize: "14px", color: "var(--text-secondary)", marginTop: "2px" }}
                  >
                    {selectedSupervisor.designationName || "No Title"} |{" "}
                    {selectedSupervisor.departmentName || "No Department"}
                  </div>
                </div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "12px", color: "var(--light-focus)" }}>Current Reports</div>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: "16px",
                    color: "var(--text-secondary)",
                    marginTop: "4px",
                  }}
                >
                  {supervisorDirectReports.length || 0}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Step 2: Placement Options */}
        {selectedSupervisor && (
          <div className="mb-4">
            <label className="step-header">Step 2: Select Placement Option</label>

            <div
              className={`placement-radio ${
                placementOption === PlacementOption.SIBLING ? "active" : ""
              }`}
              onClick={() => handlePlacementChange(PlacementOption.SIBLING)}
            >
              <div className="radio-circle">
                <input
                  type="radio"
                  checked={placementOption === PlacementOption.SIBLING}
                  readOnly
                />
              </div>
              <div className="label-group">
                <strong>Become Sibling</strong>
                <p>Employee becomes sibling to supervisor&apos;s current direct reports.</p>
                <small>
                  New Hierarchy: {selectedSupervisor.fullName} &rarr; [Existing Reports +{" "}
                  {employeeName}]
                </small>
              </div>
            </div>

            {canSelectParent && (
              <div
                className={`placement-radio mt-3 ${
                  placementOption === PlacementOption.PARENT ? "active" : ""
                }`}
                onClick={() => handlePlacementChange(PlacementOption.PARENT)}
              >
                <div className="radio-circle">
                  <input
                    type="radio"
                    checked={placementOption === PlacementOption.PARENT}
                    readOnly
                  />
                </div>
                <div className="label-group">
                  <strong>Become Parent</strong>
                  <p>Employee becomes parent to supervisor&apos;s current direct reports.</p>
                  <small>
                    New Hierarchy: {selectedSupervisor.fullName} &rarr; {employeeName} &rarr;
                    [Existing Reports]
                  </small>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Hierarchy Preview Section */}
        {selectedSupervisor && placementOption && (
          <div className="mt-4">
            <h6 className="fw-bold mb-3">Hierarchy Preview</h6>
            <div className="hierarchy-preview">
              {/* Supervisor Node */}
              <div className="hierarchy-node node-supervisor">
                <UserOutlined /> {selectedSupervisor.fullName}
                <div className="node-title small">{selectedSupervisor.designationName}</div>
              </div>

              <div className="hierarchy-arrow">
                <ArrowDownOutlined />
              </div>

              {/* PARENT OPTION LAYOUT */}
              {placementOption === PlacementOption.PARENT && (
                <div className="d-flex flex-column align-items-center">
                  <div className="hierarchy-node node-new-employee">
                    <UserAddOutlined /> {employeeName}
                    <div className="node-title small">{employeeDesignation} (NEW)</div>
                  </div>

                  <div className="hierarchy-arrow">
                    <ArrowDownOutlined />
                  </div>

                  <div className="hierarchy-children">
                    {supervisorDirectReports.map((report) => (
                      <div key={report.id} className="hierarchy-node node-child">
                        <UserOutlined /> {report.fullName}
                        <div className="node-title small">{report.designationName}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* SIBLING OPTION LAYOUT */}
              {placementOption === PlacementOption.SIBLING && (
                <div className="hierarchy-children">
                  <div className="hierarchy-node node-new-employee">
                    <UserAddOutlined /> {employeeName}
                    <div className="node-title small">{employeeDesignation} (NEW)</div>
                  </div>
                  {supervisorDirectReports.map((report) => (
                    <div key={report.id} className="hierarchy-node node-child">
                      <UserOutlined /> {report.fullName}
                      <div className="node-title small">{report.designationName}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="mt-4 text-end">
          <SoftButton
            onClick={onBack}
            className="btn-back"
            style={{ marginRight: "10px" }}
            variant="outlined"
          >
            Back
          </SoftButton>
          <SoftButton onClick={handleNext} className="btn-primary" disabled={!placementOption}>
            Next
          </SoftButton>
        </div>
      </Card>
      <Footer />
    </div>
  );
}

// REMOVED: 'employee' from PropTypes
Reporting.propTypes = {
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  jobData: PropTypes.shape({
    designationId: PropTypes.number,
    departmentId: PropTypes.number,
    fullName: PropTypes.string,
    designationName: PropTypes.string,
  }),
  initialData: PropTypes.shape({
    selectedSupervisor: PropTypes.object,
    placementOption: PropTypes.string,
  }),
};

export default Reporting;
